// Blog posts data — rich, real Madeira content.
// Each post supports HTML strings in `content` for rich rendering.

export const blogPosts = [
  {
    slug: 'top-10-levada-walks-madeira',
    title: 'Top 10 Levada Walks in Madeira: A Complete Hiker\'s Guide',
    excerpt: 'From the legendary 25 Fontes to the misty trails of Caldeirão Verde, discover the most breathtaking levada walks Madeira has to offer.',
    category: 'Hiking',
    author: 'Elvio De Andrade',
    date: '2026-01-22',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=1600&h=900&fit=crop',
    content: `
      <p>Madeira is famous worldwide for one thing in particular: its <strong>levadas</strong> — a 2,170 km network of stone-lined irrigation channels carved into the mountains over the last 500 years to bring water from the rainy north to the dry, fertile south of the island.</p>

      <p>Today, those same channels are flanked by narrow walking paths that thread through laurel forest, suspended cliffs, volcanic ridges and waterfalls. Some are gentle Sunday walks; others demand a head torch, a steady stomach and a serious pair of boots.</p>

      <h2>1. PR 6 — Levada das 25 Fontes (Rabaçal)</h2>
      <p>The classic. Starting at Rabaçal (1,064 m), this loop drops into a glacial valley where 25 springs feed a single emerald pool. It is one of the most photographed places in Portugal — and for good reason. Allow <strong>3–4 hours</strong>, moderate difficulty.</p>

      <h2>2. PR 9 — Levada do Caldeirão Verde (Santana)</h2>
      <p>A hike that culminates in a 100m waterfall plunging into a hidden green cauldron. Four tunnels along the way — bring a torch. <strong>5–6 hours</strong>, easy-to-moderate.</p>

      <h2>3. Vereda do Pico Ruivo</h2>
      <p>Not technically a levada, but no list is complete without it. From Achada do Teixeira you walk through clouds to Madeira\'s highest summit (1,862 m). Best at sunrise. <strong>3 hours round trip</strong>, easy.</p>

      <h2>4. PR 1 — Vereda do Areeiro to Pico Ruivo</h2>
      <p>The hardest hike on the island and the most spectacular. Steep stairs, exposed ridges, three tunnels and 360° views of the whole archipelago. <strong>6–7 hours one way</strong>, hard.</p>

      <h2>5. Levada do Rei (São Jorge)</h2>
      <p>One of the quietest and most beautiful trails on the north coast. Walk through pristine Laurissilva forest to a hidden spring deep in the valley. <strong>4 hours</strong>, easy.</p>

      <h2>6. Levada do Furado (PR 10)</h2>
      <p>From Ribeiro Frio to Portela through some of the densest laurel forest left in the world. UNESCO listed. <strong>4–5 hours</strong>, moderate.</p>

      <h2>7. Vereda dos Balcões</h2>
      <p>A short 1.5 km walk to one of the best viewpoints on the island. Perfect if you only have an afternoon. <strong>1 hour</strong>, easy.</p>

      <h2>8. Levada Nova / Levada do Moinho (Ponta do Sol)</h2>
      <p>A circular hike with a tunnel, two levadas and a waterfall you walk behind. <strong>3 hours</strong>, moderate.</p>

      <h2>9. Ponta de São Lourenço (PR 8)</h2>
      <p>The eastern tip of Madeira — volcanic, desert-like, completely different from the rest of the island. <strong>3 hours round trip</strong>, easy.</p>

      <h2>10. Levada do Alecrim</h2>
      <p>Starts at Rabaçal alongside the 25 Fontes trail but stays high. Ends at a small lake called Lagoa do Vento with a 100 m waterfall. <strong>2.5 hours</strong>, easy.</p>

      <h3>Before you go</h3>
      <ul>
        <li>Always carry a head torch — tunnels are pitch black.</li>
        <li>Bring 1.5 L of water minimum and a light rain jacket.</li>
        <li>Check trail status at <em>visitmadeira.pt</em> — some PRs close after storms.</li>
        <li>Wear proper hiking boots: levada edges can be slippery.</li>
      </ul>

      <p><em>Want a local guide on these trails? We run small-group hikes year-round.</em></p>
    `
  },
  {
    slug: 'best-time-to-visit-madeira',
    title: 'Best Time to Visit Madeira: A Month-by-Month Guide',
    excerpt: 'With its mild Atlantic climate, Madeira is a year-round destination — but each season has its own personality. Here is when to come, and why.',
    category: 'Travel Tips',
    author: 'Maísa Maximo',
    date: '2026-01-15',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&h=900&fit=crop',
    content: `
      <p>Madeira sits 600 km off the African coast, just close enough to the Tropic of Cancer to enjoy near-perfect weather all year round. Average temperatures rarely drop below 16°C in winter or climb above 26°C in summer.</p>

      <h2>Spring (March–May): the sweet spot</h2>
      <p>Wildflowers bloom across the island, levadas run full, and Funchal\'s famous <strong>Flower Festival</strong> takes over the city in late April. Hiking conditions are ideal. Expect occasional rain in the mountains.</p>

      <h2>Summer (June–August): high season</h2>
      <p>Warm seas (around 22°C), perfect for swimming at Porto Moniz lava pools or Prainha beach. Festas de São Pedro (29 June) and the Atlantic Festival fireworks light up Funchal every Saturday.</p>

      <h2>Autumn (September–November): the local secret</h2>
      <p>Our favourite season. The water is still warm, the crowds disappear, the laurissilva forest turns golden and the wine harvest is in full swing in São Vicente and Câmara de Lobos.</p>

      <h2>Winter (December–February): mild and dramatic</h2>
      <p>Funchal\'s New Year fireworks are listed in the Guinness Book of Records. Snow can fall on Pico Ruivo above 1,500 m — yes, real snow on a subtropical island. Whale watching is excellent.</p>

      <h3>Quick verdict</h3>
      <ul>
        <li><strong>Best for hiking:</strong> April–June and September–October.</li>
        <li><strong>Best for swimming:</strong> July–October.</li>
        <li><strong>Best for whales & dolphins:</strong> April–November.</li>
        <li><strong>Best for festivals:</strong> April (Flowers) and December (New Year).</li>
      </ul>
    `
  },
  {
    slug: 'pico-ruivo-sunrise-hike',
    title: 'Above the Clouds: Hiking Pico Ruivo at Sunrise',
    excerpt: 'A 4 a.m. wake-up, a head torch, and a path through volcanic ridges. The reward: watching the sun rise over the Atlantic from 1,862 metres.',
    category: 'Hiking',
    author: 'Elvio De Andrade',
    date: '2026-01-08',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&h=900&fit=crop',
    content: `
      <p>If you do only one hike in Madeira, make it this one.</p>

      <p>Pico Ruivo is the highest point on the island at 1,862 metres. From its tiny stone shelter, on a clear morning, you can see the whole archipelago — Porto Santo to the north-east, the Desertas to the south-east, and the Atlantic stretching to the horizon on every side. Below you, a sea of clouds.</p>

      <h2>The easy way: Achada do Teixeira</h2>
      <p>Most people start from the car park at <strong>Achada do Teixeira</strong> (1,592 m). It is a 2.8 km paved path each way, gentle enough that you can be at the summit in 45–60 minutes. Three small refuges along the way offer shelter and water.</p>

      <h2>The hard way: PR 1 from Pico do Areeiro</h2>
      <p>The PR 1 from Pico do Areeiro (1,818 m) is one of the most spectacular hikes in Europe. It crosses three peaks, two tunnels, narrow staircases carved into the cliff face, and ridges with sheer 800 m drops on each side. Allow <strong>3.5 hours one way</strong> and arrange a transfer back.</p>

      <h2>For the sunrise</h2>
      <p>Leave Achada do Teixeira no later than 1 hour before sunrise. Bring:</p>
      <ul>
        <li>Head torch (essential — the path is unlit)</li>
        <li>Warm jacket and gloves (it can be 5°C even in summer)</li>
        <li>Hot tea in a flask — you will want it at the top</li>
        <li>Camera with a wide-angle lens</li>
      </ul>

      <p>And then, sit on the stone wall, wait, and watch the world turn pink.</p>
    `
  },
  {
    slug: 'madeira-food-guide',
    title: 'Eating Like a Local: A Madeira Food Guide',
    excerpt: 'Forget the tourist menus. From espetada grilled over laurel wood to the cult-favourite bolo do caco, here is the real Madeiran kitchen.',
    category: 'Culture',
    author: 'Maísa Maximo',
    date: '2025-12-28',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1600&h=900&fit=crop',
    content: `
      <p>Madeira\'s cuisine is shaped by the Atlantic, by Portuguese tradition and by the volcanic soil that produces some of the world\'s most flavourful tropical fruit.</p>

      <h2>Espetada</h2>
      <p>Cubes of beef rubbed with garlic, salt and bay leaves, skewered on a fresh laurel stick and grilled over hot coals. Served hanging from a metal hook above your table, dripping garlicky butter onto bolo do caco below.</p>

      <h2>Bolo do Caco</h2>
      <p>A flat, round bread baked on a hot basalt stone, then split open and slathered with garlic butter. Order one at any traditional restaurant — it will arrive before you have time to read the menu.</p>

      <h2>Scabbard fish with banana</h2>
      <p><em>Espada com banana</em> sounds wrong and tastes incredible. Black scabbard fish is caught at 1,500 m depth off Câmara de Lobos. Pan-fried in butter and topped with fried slices of local banana. Trust us.</p>

      <h2>Poncha</h2>
      <p>The island\'s national drink. Three ingredients: aguardente (sugarcane spirit), lemon juice and honey. Stirred — not shaken — with a wooden pestle called a <em>mexelote</em>. The traditional version is in Câmara de Lobos at <strong>Taberna da Poncha</strong>.</p>

      <h2>Madeira wine</h2>
      <p>Fortified wine that survived the long sea voyages of the 17th century. Four styles, from dry Sercial to sweet Malmsey. The oldest bottles still being sold are from the 1840s. Visit Blandy\'s Wine Lodge in Funchal for a tasting.</p>

      <h2>Where to eat</h2>
      <ul>
        <li><strong>Santo António da Serra</strong> — Restaurante O Polar for espetada from the source.</li>
        <li><strong>Câmara de Lobos</strong> — Vila do Peixe for fresh-off-the-boat fish.</li>
        <li><strong>Funchal Mercado dos Lavradores</strong> — for tropical fruit, but watch for tourist pricing.</li>
        <li><strong>Santana</strong> — Quinta do Furão for a long lunch with an Atlantic view.</li>
      </ul>
    `
  },
  {
    slug: 'porto-moniz-natural-pools',
    title: 'Porto Moniz: Swimming in Atlantic Volcanic Pools',
    excerpt: 'Carved by lava and refilled by the ocean with every tide, the natural pools of Porto Moniz are one of Madeira\'s most extraordinary places to swim.',
    category: 'Travel Tips',
    author: 'Elvio De Andrade',
    date: '2025-12-12',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1600&h=900&fit=crop',
    content: `
      <p>On the wild north-west tip of Madeira, where the laurissilva forest meets the Atlantic, you will find one of the most photographed places on the island: the <strong>natural pools of Porto Moniz</strong>.</p>

      <p>These are not man-made. The pools were carved by lava cooling against the sea millions of years ago. The ocean refills them with every tide, so the water is always fresh, salty and crystal clear.</p>

      <h2>Two pools, two experiences</h2>
      <p>There are actually two complexes. The <strong>main paid pools</strong> have lifeguards, sun loungers, a children\'s area and changing rooms (€1.50 entry). The <strong>old free pools</strong>, 200 m up the coast, are wilder — black volcanic rock, crashing waves and zero infrastructure.</p>

      <h2>When to go</h2>
      <p>Summer (July–September) for the warmest water. Late afternoon for the best light. Avoid days with rough seas — locals will tell you when. The official pools close on red-flag days.</p>

      <h2>While you are there</h2>
      <ul>
        <li>Drive the old ER101 coastal road from São Vicente — one of the most beautiful drives in Europe.</li>
        <li>Stop at <strong>Seixal</strong> for the black-sand beach and waterfall.</li>
        <li>Eat lunch at <strong>Cachalote</strong>, perched directly above the pools.</li>
      </ul>
    `
  },
  {
    slug: 'madeira-packing-list',
    title: 'What to Pack for Madeira: The Honest Locals\' List',
    excerpt: 'Madeira is not Portugal. You can have sun, fog, rain and cold in a single hike. Here is exactly what to bring (and what to leave behind).',
    category: 'Travel Tips',
    author: 'Maísa Maximo',
    date: '2025-12-01',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&h=900&fit=crop',
    content: `
      <p>The number one mistake first-time visitors make? Packing for "Portugal in summer".</p>

      <p>Madeira has six microclimates. You can leave Funchal at 22°C, drive 30 minutes up to Pico do Areeiro, and arrive at 8°C with horizontal rain. Pack like you are going hiking in Wales — even in August.</p>

      <h2>The essentials</h2>
      <ul>
        <li><strong>Proper hiking boots</strong> — not trainers. Levada paths are wet and rocky.</li>
        <li><strong>Light waterproof jacket</strong> with a hood.</li>
        <li><strong>Fleece or warm mid-layer</strong> — for the mountains.</li>
        <li><strong>Swimwear and quick-dry towel</strong> — for the natural pools and waterfalls.</li>
        <li><strong>Head torch</strong> — many levadas have unlit tunnels.</li>
        <li><strong>Refillable water bottle</strong> — tap water is excellent.</li>
        <li><strong>SPF 50 sunscreen</strong> — the Atlantic sun is fierce.</li>
        <li><strong>European Type F plug adapter</strong>.</li>
      </ul>

      <h2>Nice to have</h2>
      <ul>
        <li>Trekking poles for the steeper trails (Pico Ruivo, PR 1).</li>
        <li>Dry bag for canyoning or sea trips.</li>
        <li>A small backpack (20–25 L) for daily hikes.</li>
      </ul>

      <h2>What to leave at home</h2>
      <ul>
        <li>Heavy jeans — too hot below 800 m, too cold above.</li>
        <li>High heels — Funchal is paved with rounded basalt cobbles. Ankles beware.</li>
        <li>An umbrella — wind will eat it for breakfast.</li>
      </ul>
    `
  }
];
